"use client";

import {
  forwardRef,
  MouseEvent,
  useLayoutEffect,
  useRef,
} from "react";

import { csx } from "../../utils/string";
import {
  isUrlExternal,
  handleInternalNavigation,
} from "../../utils/navigation";
import "./Button.css";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "translucent";

export const ButtonVariants: Record<ButtonVariant, ButtonVariant> = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  danger: "danger",
  translucent: "translucent",
};

export type ButtonSize = "small" | "medium" | "large" | "xlarge" | "2xlarge";

export const ButtonSizes: Record<ButtonSize, ButtonSize> = {
  small: "small",
  medium: "medium",
  large: "large",
  xlarge: "xlarge",
  "2xlarge": "2xlarge",
};

export interface ButtonProps {
  /** The button's visual aesthetic
   * - type {@link ButtonVariant}
   */
  variant?: ButtonVariant;

  /** The size of the button
   * - type {@link ButtonSize}
   * @default "medium"
   */
  size?: ButtonSize;

  /** Should the button be full width?
   * @default false
   */
  fullWidth?: boolean;

  /** The prefix to display within the button */
  icon?: React.ReactNode;

  /** Should the button be non-interactive? */
  isDisabled?: boolean;

  /** The URL that the button should link to. Turns the element into an `a` tag. If the URL is external, you should pass the entire URL to the `to` prop (e.g. `https://example.com`). */
  to?: string;

  /** The target attribute for the link. Defaults to `_blank` if the URL is external. */
  target?: "_blank" | "_self" | "_parent" | "_top" | string;

  /** Called when the button is pressed (on release, not keydown) */
  onPress?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;

  /** The type of button */
  type?: "button" | "submit" | "reset";

  /** The test ID for the button */
  "data-testid"?: string;

  /** The content to display within the button */
  children?: React.ReactNode;
}

/**
 * A customizable button component that can render as either a button or anchor element.
 *
 * API:
 *  - {@link ButtonProps}
 */
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      variant = "primary",
      size = "medium",
      fullWidth = false,
      icon,
      to,
      target,
      onPress,
      type = "button",
      isDisabled,
      "data-testid": testId,
      children,
    }: ButtonProps,
    ref
  ) => {
    const isExternal = to && isUrlExternal(to);
    const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

    useLayoutEffect(() => {
      if (typeof ref === "function") {
        ref(buttonRef.current);
      } else if (ref) {
        ref.current = buttonRef.current;
      }
    }, [ref]);

    const commonProps = {
      className: csx(
        "proton-Button",
        `proton-Button--${variant}`,
        fullWidth && "proton-Button--fullWidth",
        isDisabled && "proton-Button--disabled",
        size && `proton-Button--${size}`
      ),
      "data-testid": testId,
      ...(to && { "aria-disabled": isDisabled }),
      ...(isDisabled && { tabIndex: -1 }),
    };

    const content = (
      <>
        {icon && (
          <div
            className={csx(
              "proton-Button__icon-decorator",
              fullWidth && "proton-Button__icon-decorator--fullWidth"
            )}
          >
            {icon}
          </div>
        )}
        {children && <div className="proton-Button__text">{children}</div>}
      </>
    );

    if (to) {
      return (
        <a
          {...commonProps}
          href={to}
          target={target || (isExternal ? "_blank" : undefined)}
          rel={
            isExternal || target === "_blank"
              ? "noopener noreferrer"
              : undefined
          }
          ref={ref as React.RefObject<HTMLAnchorElement>}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }

            if (!isExternal && !target) {
              handleInternalNavigation(e, to);
            }

            onPress?.(e);
          }}
          role="button"
        >
          {content}
        </a>
      );
    }

    return (
      <button
        {...commonProps}
        type={type}
        disabled={isDisabled}
        ref={ref as React.RefObject<HTMLButtonElement>}
        onClick={(e) => {
          if (!isDisabled && onPress) {
            onPress(e);
          }
        }}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "ProtonUIButton";
